import { PlanPremiumIcon, PlanPremiumFridayIcon } from 'src/assets'
import peopleOutline from '@iconify-icons/eva/people-outline'
import link2Outline from '@iconify-icons/eva/link-2-outline'
import creditCardOutline from '@iconify-icons/eva/credit-card-outline'
import fileTextOutline from '@iconify-icons/eva/file-text-outline'
import flashOutline from '@iconify-icons/eva/flash-outline'
import lockOutline from '@iconify-icons/eva/lock-outline'
import awardOutline from '@iconify-icons/eva/award-outline'
import archiveOutline from '@iconify-icons/eva/archive-outline'
import globeOutline from '@iconify-icons/eva/globe-outline'
import codeOutline from '@iconify-icons/eva/code-outline'
import layersOutline from '@iconify-icons/eva/layers-outline'
import cubeOutline from '@iconify-icons/eva/cube-outline'
import bulbOutline from '@iconify-icons/eva/bulb-outline'
import browserOutline from '@iconify-icons/eva/browser-outline'
import searchOutline from '@iconify-icons/eva/search-outline'
import swapOutline from '@iconify-icons/eva/swap-outline'
import smartphoneOutline from '@iconify-icons/eva/smartphone-outline'
import logInOutline from '@iconify-icons/eva/log-in-outline'
import messageCircleOutline from '@iconify-icons/eva/message-circle-outline'
import barChartOutline from '@iconify-icons/eva/bar-chart-outline'
import { AUDIENCES } from './pricing-json'
import { BOOK_A_CALL_GROWTH_URL, BOOK_A_CALL_HEADLESS_URL } from '../logic'

// Plus and Pro v2 are RoleType 33/34 in mcg-backend-net, and must stay in step
// with the StripePriceIds on those enum members.
// Live products: prod_V0K4uU0ajvrnNw / prod_V0K43tLS8qt3ab.
// The enterprise plans are sold as the existing Growth (standalone academy) and
// Scale (headless academy) subscriptions: same annual
// Stripe prices and role types, renamed on the page. Launch is no longer sold;
// its subscribers keep managing their plan in the Stripe customer portal.
const CREATOR_PLUS_MONTHLY = 'price_1U0JQ6I6xu65zAZT8coDmW02'
const CREATOR_PLUS_ANNUAL = 'price_1U0JQ7I6xu65zAZTHMEUvnrO'
const CREATOR_PRO_MONTHLY = 'price_1U0JQ8I6xu65zAZTgADsU1ih'
const CREATOR_PRO_ANNUAL = 'price_1U0JQ9I6xu65zAZTuEQL3c2P'
const GROWTH_MONTHLY = 'price_1T2RrUI6xu65zAZTdPdslybm'
const GROWTH_ANNUAL = 'price_1SzyTsI6xu65zAZTGoWWt82P'
const SCALE_MONTHLY = 'price_1T260oI6xu65zAZTTMiGX3O4'
const SCALE_ANNUAL = 'price_1T261VI6xu65zAZTbMv0eepY'

const LOGO = name => `/assets/pricing/logo-${name}.webp`

const list = texts => texts.map(text => ({ text, isAvailable: true }))

// Plan cards (Tally-style): each feature has an icon, a bold title and a
// one-line description. `lists` is derived from the titles for other consumers.
const features = items => ({
  details: items,
  lists: list(items.map(item => item.title)),
})

// Enterprise "All Plans Include": what both business plans share. Keep in sync
// with `enterpriseAllPlansInclude` in mcg-next-site src/lib/pricing.ts.
const ENTERPRISE_ALL_PLANS_INCLUDE = [
  'Everything in Creator Pro',
  'Embeddable Widgets with User Tracking',
  'Webhooks, Zapier & Make Automations',
  'Unlimited Custom Domains',
  'Invitation-only Access',
  'Certifications',
  'AI Course Translation',
  'Export to Dynamic SCORM',
  '3,000 AI Credits per Seat / Month',
  'Analytics & Reporting',
  'MCP Access',
]

const PLANS = ({ timeInterval, groupId, getAction, getUpgradeAction }) => {
  const plan = card => ({
    ...card,
    isAnnual: timeInterval,
    groupId,
    action: getAction(groupId, card.key),
  })

  return [
    // Creator "All Plans Include": shared features that don't fit on the Plus
    // card (Plus lists its own features plus MCP and the AI Course Creator).
    {
      subscription: 'All Plans Include',
      isAllPlansInclude: true,
      audience: AUDIENCES.CREATOR,
      icon: <PlanPremiumIcon />,
      price: null,
      groupId,
      caption:
        'Experience the best e-learning authoring solution with your complete AI assistant.',
      lists: list([
        'AI Interaction Builder',
        'AI Educational Game Builder',
        'AI YouTube-to-Course Builder',
        'AI Lesson Page Generator',
        'Community Boards and AI Moderator',
        'Paths, Badges & Rewards',
        'Public Courses (Open or Email-Verified)',
        'Sharing by Link & Embed',
        'AI Voice Over & Accessibility Widget',
        'Analytics & Reporting',
        'Landing Pages',
        'Collaboration',
      ]),
      action: {
        text: '',
        disabled: true,
        background: '#F4F6F8',
      },
    },

    // ---- Creator ----
    plan({
      key: 'plus',
      audience: AUDIENCES.CREATOR,
      subscription: 'Plus',
      roleName: 'Plus',
      icon: <PlanPremiumIcon />,
      price: timeInterval ? 39 : 49,
      priceId: timeInterval ? CREATOR_PLUS_ANNUAL : CREATOR_PLUS_MONTHLY,
      priceTrialDays: null,
      successReturnUrl: timeInterval
        ? 'annual-plus-subscription-successful'
        : 'plus-subscription-successful',
      caption: 'For creators launching and selling their first courses.',
      savings: 120,
      ...features([
        {
          icon: peopleOutline,
          title: '500 Learners / Year',
          description: 'Reach up to 500 learners every year.',
        },
        {
          icon: link2Outline,
          title: '1 Custom Domain',
          description: 'Host your courses on your own (sub)domain.',
        },
        {
          icon: creditCardOutline,
          title: 'Paid Courses',
          description: 'Sell access to your courses and get paid directly.',
        },
        {
          icon: fileTextOutline,
          title: 'Export to PDF',
          description: 'Download your courses as PDFs to share anywhere.',
        },
        {
          icon: flashOutline,
          title: '1,000 AI Credits / Month',
          description: 'Generate courses, images and quizzes with AI.',
        },
        {
          icon: cubeOutline,
          title: 'MCP Access',
          description:
            'Build and edit courses from Claude, ChatGPT and other AI assistants.',
        },
        {
          icon: bulbOutline,
          title: 'AI Course Creator with AI Images & Quizzes',
          description:
            'Turn a topic into a full course, complete with images and quizzes.',
        },
      ]),
    }),
    plan({
      key: 'pro',
      audience: AUDIENCES.CREATOR,
      subscription: 'Pro',
      roleName: 'Pro v2',
      icon: <PlanPremiumIcon />,
      price: timeInterval ? 79 : 99,
      priceId: timeInterval ? CREATOR_PRO_ANNUAL : CREATOR_PRO_MONTHLY,
      priceTrialDays: null,
      successReturnUrl: timeInterval
        ? 'annual-pro-v2-subscription-successful'
        : 'pro-v2-subscription-successful',
      caption: 'For creators scaling their audience with advanced tools.',
      savings: 240,
      ...features([
        {
          icon: layersOutline,
          title: 'Everything in Plus',
          description: 'Plus the following tools to grow your audience.',
        },
        {
          icon: peopleOutline,
          title: '1,000 Learners / Year',
          description: 'Twice the yearly learner allowance of Plus.',
        },
        {
          icon: link2Outline,
          title: '3 Custom Domains',
          description: 'Run separate branded sites for each audience.',
        },
        {
          icon: flashOutline,
          title: '3,000 AI Credits / Month',
          description: 'Three times the AI credits of Plus.',
        },
        {
          icon: lockOutline,
          title: 'Invitation-only Access',
          description: 'Keep courses private to the learners you invite.',
        },
        {
          icon: awardOutline,
          title: 'Certifications',
          description: 'Award certificates when learners complete a course.',
        },
        {
          icon: globeOutline,
          title: 'AI Course Translation',
          description: 'Translate your courses into other languages with AI.',
        },
        {
          icon: archiveOutline,
          title: 'Export to Dynamic SCORM',
          description: "Deliver your courses inside your customers' own LMS.",
        },
        {
          icon: codeOutline,
          title: 'Webhooks',
          description: 'Send learner activity to your CRM and other tools.',
        },
      ]),
    }),

    // ---- Enterprise: two use-case plans, billed yearly, sold via demo ----
    {
      subscription: 'All Plans Include',
      isAllPlansInclude: true,
      audience: AUDIENCES.ENTERPRISE,
      icon: <PlanPremiumFridayIcon />,
      price: null,
      groupId,
      caption:
        'Everything your team needs to run customer and partner academies.',
      lists: list(ENTERPRISE_ALL_PLANS_INCLUDE),
      action: {
        text: '',
        disabled: true,
        background: '#F4F6F8',
      },
    },
    {
      key: 'marketing',
      audience: AUDIENCES.ENTERPRISE,
      subscription: 'Build Academy for Growth',
      nameHighlight: 'Growth',
      useCase: 'Standalone',
      isAnnual: timeInterval,
      demoUrl: BOOK_A_CALL_GROWTH_URL,
      roleName: 'Growth',
      priceId: timeInterval ? GROWTH_ANNUAL : GROWTH_MONTHLY,
      priceTrialDays: null,
      successReturnUrl: timeInterval
        ? 'annual-elite-subscription-successful'
        : 'elite-subscription-successful',
      icon: <PlanPremiumFridayIcon />,
      price: timeInterval ? 279 : 349,
      savings: 840,
      groupId,
      caption:
        'Launch a standalone academy on your custom domain to generate leads, enable sales, and educate your market.',
      learners: 'Up to 5,000 learners / year',
      ...features([
        {
          icon: peopleOutline,
          title: '3 Team Seats',
          description: 'Run the academy together with your marketing team.',
        },
        {
          icon: codeOutline,
          title: 'Basic API',
          description:
            'Enroll learners and read their progress and completions.',
        },
        {
          icon: browserOutline,
          title: 'Branded Academy Website',
          description: 'Give your courses a home on your own branded site.',
        },
        {
          icon: searchOutline,
          title: 'Course Landing Pages with SEO Settings',
          description: 'Rank and convert with a page for every course.',
        },
        {
          icon: creditCardOutline,
          title: 'Paid Courses & Email-Verified Access',
          description: 'Sell courses or capture verified emails before access.',
        },
        {
          icon: swapOutline,
          title: 'Lead Capture into Your CRM via Webhooks',
          description: 'Send every signup and completion to your CRM.',
        },
        {
          icon: awardOutline,
          title: 'Certificates of Completion',
          description: 'Reward learners when they finish a course.',
        },
      ]),
      logos: [
        { src: LOGO('magicschool'), alt: 'MagicSchool' },
        { src: LOGO('brisk'), alt: 'Brisk Teaching' },
        { src: LOGO('teachy'), alt: 'Teachy' },
      ],
      action: getAction(groupId, 'marketing'),
      upgradeAction: getUpgradeAction(groupId, 'marketing'),
    },
    {
      key: 'inapp',
      audience: AUDIENCES.ENTERPRISE,
      subscription: 'Build Academy for Customer Success',
      nameHighlight: 'Customer Success',
      useCase: 'Headless',
      isAnnual: timeInterval,
      demoUrl: BOOK_A_CALL_HEADLESS_URL,
      roleName: 'Scale',
      priceId: timeInterval ? SCALE_ANNUAL : SCALE_MONTHLY,
      priceTrialDays: null,
      successReturnUrl: timeInterval
        ? 'annual-scale-subscription-successful'
        : 'scale-subscription-successful',
      icon: <PlanPremiumFridayIcon />,
      price: timeInterval ? 579 : 723,
      savings: 1728,
      groupId,
      caption:
        'Embed academy directly inside your web or mobile app using our Headless LMS for onboarding, feature adoption and retention.',
      learners: 'Up to 5,000 learners / year',
      ...features([
        {
          icon: peopleOutline,
          title: 'Unlimited Team Seats',
          description: 'Give every team that touches onboarding access.',
        },
        {
          icon: codeOutline,
          title: 'Full REST API',
          description:
            'Manage learners, courses and results from your own systems.',
        },
        {
          icon: smartphoneOutline,
          title: 'Headless LMS for Mobile and Web Apps',
          description: 'Show courses natively inside your product.',
        },
        {
          icon: logInOutline,
          title: 'In-App Authentication',
          description: 'Learners sign in with their existing product account.',
        },
        {
          icon: messageCircleOutline,
          title: 'MCG App for Intercom',
          description: 'Deliver courses right inside Intercom Messenger.',
        },
        {
          icon: barChartOutline,
          title: "Account-based Reporting for your Customers' Admins",
          description: "Let each customer's admin see their team's progress.",
        },
        {
          icon: logInOutline,
          title: 'Single Sign-On (SSO)',
          description: 'Let learners in with your own identity provider.',
        },
        {
          icon: awardOutline,
          title: 'Enterprise SLA',
          description: 'Guaranteed uptime and response times, in writing.',
        },
        {
          icon: peopleOutline,
          title: 'Dedicated Customer Success Manager',
          description: 'A named contact who helps you launch and grow.',
        },
      ]),
      logos: [
        { src: LOGO('enerflo'), alt: 'Enerflo' },
        { src: LOGO('visibuild'), alt: 'Visibuild' },
        { src: LOGO('aloha'), alt: 'Aloha' },
        { src: LOGO('sunday'), alt: 'Sunday' },
      ],
      action: getAction(groupId, 'inapp'),
      upgradeAction: getUpgradeAction(groupId, 'inapp'),
    },
  ]
}

export default PLANS
